import { useTranslation } from 'react-i18next';
import { ATTRIBUTION_FIELDS, classifySource, readMeta } from './attribution';
import { Tag } from '@/lib/ui/Tag';
import { MonoValue, Eyebrow } from '@/lib/ui/Typography';

/** The lead's normalized channel as a tag; "—" when the lead predates attribution. */
export function SourceTag({ source }: { source: unknown }) {
  const { t } = useTranslation();
  const c = classifySource(source);
  switch (c.kind) {
    case 'known':
      return (
        <Tag variant={c.channel === 'direct' ? 'neutral' : 'teal'}>
          {t(`leads.source.channel.${c.channel}`)}
        </Tag>
      );
    case 'referral':
      return (
        <Tag variant="neutral">
          {/* The host is an LTR token inside a possibly-RTL label. */}
          {t('leads.source.channel.referral')} · <bdi dir="ltr">{c.host}</bdi>
        </Tag>
      );
    case 'other':
      return (
        <Tag variant="neutral">
          <bdi dir="ltr">{c.value}</bdi>
        </Tag>
      );
    case 'none':
      return <span className="text-ink-mute">—</span>;
  }
}

/**
 * "Source" section of the lead detail: channel, campaign parameters, click ids, landing
 * path, referrer and user agent. Every field is always listed, "—" when empty, so an
 * operator can tell "not captured" from "not shown". The visitor IP is never rendered.
 */
export function LeadSourcePanel({ source, meta }: { source: unknown; meta: unknown }) {
  const { t } = useTranslation();
  return (
    <section
      aria-labelledby="lead-source-heading"
      className="flex flex-col gap-md border-t-[0.5px] border-hairline pt-lg"
    >
      <div className="flex flex-wrap items-center gap-sm">
        <h3 id="lead-source-heading">
          <Eyebrow>{t('leads.source.heading')}</Eyebrow>
        </h3>
        <SourceTag source={source} />
      </div>
      <dl className="grid grid-cols-1 gap-md sm:grid-cols-2">
        {ATTRIBUTION_FIELDS.map((f) => {
          const v = readMeta(meta, f.key);
          return (
            <div key={f.key} className="min-w-0">
              <dt className="eyebrow text-ink-mute">{t(f.labelKey)}</dt>
              <dd className="mt-2xs break-all text-small text-ink-graphite">
                {v ? (
                  <MonoValue>
                    <bdi dir="ltr">{v}</bdi>
                  </MonoValue>
                ) : (
                  <span className="text-ink-mute">—</span>
                )}
              </dd>
            </div>
          );
        })}
      </dl>
    </section>
  );
}
